import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { Trip } from '../../types';
import { formatDate, formatCurrency, getPaymentStatusLabel, getPaymentStatusColor, isPaymentPaid } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import { isPaymentEnabled } from '../../utils/mockHelper';
import QRCodeDisplay from '../../components/QRCodeDisplay';
import QRCodeModal from '../../components/QRCodeModal';
import { useQRCode } from '../../hooks/useQRCode';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  Users, 
  CreditCard, 
  Clock,
  Download,
  Share2,
  Edit,
  CheckCircle,
  AlertCircle,
  FileText,
  QrCode
} from 'lucide-react';

const TripDetailsPage: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trips, loading, error } = useApp();
  
  const [trip, setTrip] = useState<Trip | null>(null);
  const [showQRCodes, setShowQRCodes] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [qrCodeModal, setQrCodeModal] = useState<{
    isOpen: boolean;
    voucherCode: string;
    participantName?: string;
  }>({
    isOpen: false,
    voucherCode: '',
    participantName: ''
  });
  const { downloadAllQRCodes, isGenerating } = useQRCode();

  useEffect(() => {
    if (!tripId) {
      navigate('/trips');
      return;
    }

    // Encontrar a viagem nos dados já carregados
    const foundTrip = trips?.find(t => t.id === parseInt(tripId));
    if (foundTrip) {
      setTrip(foundTrip);
      setIsLoading(false);
    } else if (!loading.trips) {
      // Se não está carregando e não encontrou, redirecionar
      navigate('/trips');
    }
  }, [tripId, trips, loading.trips, navigate]);

  if (isLoading || loading.trips) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error.trips) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert type="error" title="Erro ao carregar viagem">
            {error.trips}
          </Alert>
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert type="warning" title="Viagem não encontrada">
            A viagem que você está procurando não foi encontrada.
          </Alert>
        </div>
      </div>
    );
  }

  const getTripStatus = () => {
    const now = new Date();
    const startDate = new Date(trip.start_date);
    const endDate = new Date(trip.end_date);
    
    if (startDate <= now && now <= endDate) {
      return { status: 'current', label: 'Em andamento', color: 'text-blue-600 bg-blue-100', icon: Clock };
    } else if (startDate > now) {
      return { status: 'upcoming', label: 'Próxima', color: 'text-green-600 bg-green-100', icon: Calendar };
    } else {
      return { status: 'past', label: 'Finalizada', color: 'text-gray-600 bg-gray-100', icon: CheckCircle };
    }
  };

  const tripStatus = getTripStatus();
  const StatusIcon = tripStatus.icon;
  const isPaid = isPaymentPaid(trip.payment_status_id);

  const handleDownloadAllQRCodes = async () => {
    if (!trip) return;
    await downloadAllQRCodes(trip);
  };

  const handleShareTrip = async () => {
    const tripUrl = `${window.location.origin}/trips/${trip.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Viagem - ${trip.hotel_name}`,
          text: `Confira os detalhes da viagem para ${trip.hotel_name}`,
          url: tripUrl
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      // Fallback: copiar URL para clipboard
      try {
        await navigator.clipboard.writeText(tripUrl);
        alert('Link da viagem copiado para a área de transferência!');
      } catch (err) {
        console.error('Error copying to clipboard:', err);
      }
    }
  };

  const handleViewQRCode = (voucherCode: string, participantName?: string) => {
    setQrCodeModal({
      isOpen: true,
      voucherCode,
      participantName
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to="/trips"
              className="p-2 hover:bg-white hover:shadow-md rounded-lg transition-all duration-200"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 break-words">{trip.hotel_name}</h1>
              <p className="text-gray-600 flex items-center space-x-2">
                <StatusIcon className="h-4 w-4 flex-shrink-0" />
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${tripStatus.color}`}>
                  {tripStatus.label}
                </span>
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 mt-4 sm:mt-0">
            {isPaid && (
              <button
                onClick={() => setShowQRCodes(!showQRCodes)}
                className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  showQRCodes 
                    ? 'text-blue-700 bg-blue-100 hover:bg-blue-200' 
                    : 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                }`}
              >
                <QrCode className="h-4 w-4 mr-2" />
                {showQRCodes ? 'Ocultar QR Codes' : 'Ver QR Codes'}
              </button>
            )}
            <button
              onClick={handleShareTrip}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Compartilhar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Info Card */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                Informações da Viagem
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Hotel/Pousada</label>
                    <p className="text-gray-900 font-medium break-words">{trip.hotel_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Data de Check-in</label>
                    <p className="text-gray-900 flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                      <span className="break-words">{formatDate(trip.start_date)}</span>
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Data de Check-out</label>
                    <p className="text-gray-900 flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                      <span className="break-words">{formatDate(trip.end_date)}</span>
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Total de Dias</label>
                    <p className="text-gray-900 font-medium">{trip.total_days} dia{trip.total_days !== 1 ? 's' : ''}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Total de Participantes</label>
                    <p className="text-gray-900 flex items-center">
                      <Users className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                      <span>{trip.participants.length} participante{trip.participants.length !== 1 ? 's' : ''}</span>
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Status do Pagamento</label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPaymentStatusColor(trip.payment_status_id)}`}>
                      {isPaid ? <CheckCircle className="h-4 w-4 mr-1 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />}
                      <span className="break-words">{getPaymentStatusLabel(trip.payment_status_id)}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Participants List */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center mb-4 sm:mb-0">
                  <Users className="h-5 w-5 text-blue-600 mr-2" />
                  Participantes
                </h2>
                {isPaid && (
                  <button
                    onClick={handleDownloadAllQRCodes}
                    disabled={isGenerating}
                    className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200 disabled:opacity-50 w-full sm:w-auto"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {isGenerating ? 'Gerando...' : 'Download Todos QR Codes'}
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {trip.participants.map((participant) => (
                  <div
                    key={participant.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 space-y-3 sm:space-y-0"
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 break-words">{participant.name}</h3>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm text-gray-600 mt-1 space-y-1 sm:space-y-0">
                        {participant.email && (
                          <span className="break-words">{participant.email}</span>
                        )}
                        <span className="break-words">CPF: {participant.document_number}</span>
                        {participant.phone && (
                          <span className="break-words">Tel: {participant.country_code || '+55'} {participant.phone}</span>
                        )}
                        {participant.is_exempt && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-green-700 bg-green-100 self-start">
                            Isento
                          </span>
                        )}
                      </div>
                      {participant.voucher_code && (
                        <div className="mt-2">
                          <span className="text-xs text-gray-500">Voucher: </span>
                          <code className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded break-all">
                            {participant.voucher_code}
                          </code>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-row sm:flex-col sm:text-right items-center sm:items-end justify-between sm:justify-start space-x-2 sm:space-x-0">
                      <p className="font-medium text-gray-900 text-lg sm:text-base">
                        {formatCurrency(participant.individual_amount || 0)}
                      </p>
                      {isPaid && participant.voucher_code && (
                        <button 
                          onClick={() => handleViewQRCode(participant.voucher_code || '', participant.name)}
                          className="text-sm text-blue-600 hover:text-blue-700 transition-colors whitespace-nowrap"
                        >
                          Ver QR Code
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Payment Summary */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCard className="h-5 w-5 text-blue-600 mr-2" />
                Resumo do Pagamento
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm sm:text-base">Taxa Turística Total:</span>
                  <span className="font-medium text-gray-900 text-sm sm:text-base">{formatCurrency(trip.total_amount)}</span>
                </div>
                
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-900 text-sm sm:text-base">Total a Pagar:</span>
                    <span className="font-bold text-lg sm:text-xl text-blue-600">{formatCurrency(trip.total_amount)}</span>
                  </div>
                </div>
              </div>

              {!isPaid && isPaymentEnabled() && (
                <div className="mt-6">
                  <button className="w-full inline-flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Realizar Pagamento
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações</h3>
              
              <div className="space-y-3">
                {trip.is_owner && !isPaid && (
                  <button className="w-full inline-flex items-center justify-center px-4 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200 text-sm sm:text-base">
                    <Edit className="h-4 w-4 mr-2" />
                    Editar Viagem
                  </button>
                )}
                
                <button className="w-full inline-flex items-center justify-center px-4 py-2 text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 text-sm sm:text-base">
                  <FileText className="h-4 w-4 mr-2" />
                  Ver Histórico
                </button>
                
                {isPaid && (
                  <button className="w-full inline-flex items-center justify-center px-4 py-2 text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors duration-200 text-sm sm:text-base">
                    <Download className="h-4 w-4 mr-2" />
                    Reimprimir Vouchers
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* QR Codes Section */}
        {showQRCodes && isPaid && (
          <div className="mt-8">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <QrCode className="h-5 w-5 text-blue-600 mr-2" />
                QR Codes dos Participantes
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {trip.participants
                  .filter(participant => participant.voucher_code)
                  .map((participant) => (
                    <QRCodeDisplay
                      key={participant.id}
                      voucherCode={participant.voucher_code!}
                      participantName={participant.name}
                      hotelName={trip.hotel_name}
                      startDate={trip.start_date}
                      endDate={trip.end_date}
                      onDownload={() => console.log(`Downloaded QR for ${participant.name}`)}
                      onShare={() => console.log(`Shared QR for ${participant.name}`)}
                    />
                  ))
                }
              </div>

              {trip.participants.filter(p => p.voucher_code).length === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <QrCode className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 px-4">
                    QR Codes serão gerados automaticamente após a confirmação do pagamento.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* QR Code Modal */}
        <QRCodeModal
          isOpen={qrCodeModal.isOpen}
          onClose={() => setQrCodeModal({ isOpen: false, voucherCode: '', participantName: '' })}
          voucherCode={qrCodeModal.voucherCode}
          participantName={qrCodeModal.participantName}
        />
      </div>
    </div>
  );
};

export default TripDetailsPage;
